import type { Task } from "@gearbox-protocol/deploy-tools-shared";
import { cn, GrafanaLink, SourceLink } from "@gearbox-protocol/deploy-tools-ui";
import StatusDot from "components/StatusDot";
import { format, formatDistanceToNow } from "date-fns";
import type { FC, PropsWithChildren } from "react";

interface Props {
  task: Task;
}

const TaskCommonTable: FC<Props> = ({ task }) => {
  const { createdAt, updatedAt, status } = task;
  const createdStr = formatDistanceToNow(new Date(createdAt), {
    addSuffix: true,
  });
  const updatedStr = formatDistanceToNow(new Date(updatedAt), {
    addSuffix: true,
  });
  return (
    <table className="table-auto border-collapse border border-slate-500">
      <tbody>
        <Row label="ID">{task.id}</Row>

        <Row label="Definition">{`${task.definitionId} (${task.family})`}</Row>

        <Row label="Logs">
          <GrafanaLink
            executionId={task.executionId}
            labels={{ role: task.id }}
          >
            Grafana
          </GrafanaLink>
        </Row>

        <Row label="Created at">
          <span>{createdStr}</span>
          <span className="text-slate-500">{` (${format(new Date(createdAt), "HH:mm:ss dd/MM/yyyy")})`}</span>
        </Row>

        <Row label="Status" className="flex flex-row items-center gap-2">
          <StatusDot status={status} />
          <span className="uppercase">{status}</span>
          <span>{updatedStr}</span>
          <span className="text-slate-500">{` (${format(new Date(createdAt), "HH:mm:ss dd/MM/yyyy")})`}</span>
        </Row>

        <Row label="Image">{task.image}</Row>

        {!!task.source && (
          <Row label="Source">
            <SourceLink source={task.source} revision={task.revision} />
          </Row>
        )}

        {task.entrypoint && (
          <Row label="Entrypoint">{task.entrypoint.join(" ")}</Row>
        )}

        {task.command && <Row label="Command">{task.command.join(" ")}</Row>}

        {!!task.env && (
          <Row label="Environment variables">
            <ul className="list-disc pl-4 font-mono">
              {Object.entries(task.env).map(([key, value]) => (
                <li key={key}>{`${key}=${value}`}</li>
              ))}
            </ul>
          </Row>
        )}

        {!!task.secrets && (
          <Row label="Secrets">
            <ul className="list-disc pl-4 font-mono">
              {Object.entries(task.secrets).map(([key, value]) => (
                <li key={key}>{`${key}=${value}`}</li>
              ))}
            </ul>
          </Row>
        )}
      </tbody>
    </table>
  );
};

interface RowProps {
  label: string;
  className?: string;
}

const Row: FC<PropsWithChildren<RowProps>> = ({
  label,
  children,
  className,
}) => {
  return (
    <tr>
      <td className="border border-slate-500 p-2 min-w-32">{label}</td>
      <td className={cn("border border-slate-500 p-2", className)}>
        {children}
      </td>
    </tr>
  );
};

export default TaskCommonTable;
